import {put, call, all} from 'redux-saga/effects';
import InvoiceBatchItem from "../../entities/invoiceBatchItem";
import * as Actions from "../actions/invoiceBatchItemActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type InvoiceBatchItemAction = {
    invoiceBatchItemId: string;
    invoiceBatchItem: InvoiceBatchItem;
    invoiceBatchItems: InvoiceBatchItem[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneInvoiceBatchItem(action: InvoiceBatchItemAction) {
	const session = tabSession.captureGeneration();
	try {
		const invoiceBatchItem: InvoiceBatchItem = yield call(api.findOne, 'invoiceBatchItem', action.invoiceBatchItemId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCHITEM_FETCH_ONE_SUCCESS, invoiceBatchItemId: action.invoiceBatchItemId, invoiceBatchItem, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCHITEM_FETCH_ONE_FAILURE, invoiceBatchItemId: action.invoiceBatchItemId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {InvoiceBatchItemAction} action
 */
export function* saveInvoiceBatchItem(action: InvoiceBatchItemAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ INVOICEBATCHITEM_SAVE saga', action.invoiceBatchItem?.id);

		if (action.invoiceBatchItem === undefined || action.invoiceBatchItem === null) {
			console.error('saveInvoiceBatchItem received a null');
			throw new Error('saveInvoiceBatchItem received a null');
		}

		if (!(action.invoiceBatchItem instanceof InvoiceBatchItem)) {
			action.invoiceBatchItem = new InvoiceBatchItem(action.invoiceBatchItem);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.invoiceBatchItem.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.invoiceBatchItem);
			const addResult = yield call(idb.add, 'invoiceBatchItem', action.invoiceBatchItem);
			if (addResult.isErr()) {
				console.error(`⮂ saveInvoiceBatchItem: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertInvoiceBatchItemSave(action.invoiceBatchItem, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.INVOICEBATCHITEM_SAVE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.INVOICEBATCHITEM_SAVE, invoiceBatchItem: action.invoiceBatchItem, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertInvoiceBatchItemSave(action.invoiceBatchItem, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.INVOICEBATCHITEM_SAVE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {InvoiceBatchItemAction} action
 * @param {InvoiceBatchItem[]} action.invoiceBatchItems
 */
export function* saveManyInvoiceBatchItems(action: InvoiceBatchItemAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ INVOICEBATCHITEM_SAVE_MANY saga', action.invoiceBatchItems.map(invoiceBatchItem => invoiceBatchItem.id).join(','));

        if (!Array.isArray(action.invoiceBatchItems)) {
            console.error('saveManyInvoiceBatchItems expected an array, received', action.invoiceBatchItems);
            throw new Error('saveManyInvoiceBatchItems expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveInvoiceBatchItem), under these queue keys
		const dispatchedAddedOns: number[] = action.invoiceBatchItems.map(invoiceBatchItem => invoiceBatchItem?.addedOn);
		const revertAll = () => action.invoiceBatchItems.map((invoiceBatchItem, i) =>
			put(Actions.revertInvoiceBatchItemSave(invoiceBatchItem, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: InvoiceBatchItem[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.invoiceBatchItems.length; i++) {
				const invoiceBatchItem = action.invoiceBatchItems[i];
				invoiceBatchItem.persisted = false;
				ensureOperationId(invoiceBatchItem);
				toSave.push(invoiceBatchItem);
			}

			const addResult = yield call(idb.add, 'invoiceBatchItem', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyInvoiceBatchItems: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.INVOICEBATCHITEM_SAVE_MANY_FAILURE, invoiceBatchItems: action.invoiceBatchItems, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.INVOICEBATCHITEM_SAVE_MANY, invoiceBatchItems: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.INVOICEBATCHITEM_SAVE_MANY_FAILURE, invoiceBatchItems: action.invoiceBatchItems, status: 500, err});

			console.error(`Error saving ${action.invoiceBatchItems.length} invoiceBatchItems to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {InvoiceBatchItemAction} action
 */
export function* removeInvoiceBatchItem(action: InvoiceBatchItemAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.invoiceBatchItem === undefined || action.invoiceBatchItem === null || !(action.invoiceBatchItem instanceof InvoiceBatchItem)) {
			console.error('removeInvoiceBatchItem expected type InvoiceBatchItem, received', action.invoiceBatchItem);
			reportQueued(false);
			throw new Error('removeInvoiceBatchItem expected type InvoiceBatchItem');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.invoiceBatchItem.status = 'D';
		action.invoiceBatchItem.addedOn = newHRTimestamp();
		action.invoiceBatchItem.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.invoiceBatchItem);
		const addResult = yield call(idb.add, 'invoiceBatchItem', action.invoiceBatchItem);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeInvoiceBatchItem: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.INVOICEBATCHITEM_REMOVE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCHITEM_REMOVE, invoiceBatchItem: action.invoiceBatchItem});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCHITEM_REMOVE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this InvoiceBatchItem.
	*
	* @param {InvoiceBatchItemAction} action
	*/
export function* restoreInvoiceBatchItem(action: InvoiceBatchItemAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreInvoiceBatchItem expected getCurrentUserId() to return a value');
        return;
    }

	if (action.invoiceBatchItem === undefined || action.invoiceBatchItem === null) {
        console.error('restoreInvoiceBatchItem expected a InvoiceBatchItem, received null');
        throw new Error('restoreInvoiceBatchItem expected a InvoiceBatchItem, received null');
    }
    else if (!(action.invoiceBatchItem instanceof InvoiceBatchItem)) action.invoiceBatchItem = new InvoiceBatchItem(action.invoiceBatchItem); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.invoiceBatchItem.status = 'U';
		action.invoiceBatchItem.addedOn = newHRTimestamp();
		action.invoiceBatchItem.addedBy = userId;
		action.invoiceBatchItem.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.invoiceBatchItem);
		const addResult = yield call(idb.add, 'invoiceBatchItem', action.invoiceBatchItem);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreInvoiceBatchItem: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICEBATCHITEM_RESTORE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCHITEM_RESTORE, invoiceBatchItem: action.invoiceBatchItem});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCHITEM_RESTORE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, err});

		console.error('Error restoring from database', err);
	}
}